import React, {useEffect, useContext, useCallback} from "react";
import { useHistory, useParams, useLocation } from "react-router-dom";
import { Button, SpaceBetween } from "@amzn/awsui-components-react";
import ServiceCollection from "src/services/ServiceCollection";
import { GlobalAppContext } from "../../App";
import { GLOBAL_ACTIONS } from "src/services/global/GlobalReducer";
import DynamicBreadcrumb from "src/models/navigation/DynamicBreadcrumb";
import CONSTANTS from "src/utils/constants";
import {initialState, JESettingsState} from "src/services/calculation-builder/JESettingsState";
import TPEAction from "src/models/common/TPEAction";
import {ACTIONS, jeSettingsReducer} from 'src/services/calculation-builder/JESettingsReducer';
import {UserProfile} from "src/models/users/UserProfile";
import useReducerWithLogger from "src/services/utils/ReducerWithLogger";
import UploadJournalEntryFile from "src/components/calculation-builder/configure-journals/UploadJournalEntryFile";
import JournalEntriesGrid from "src/components/calculation-builder/configure-journals/JournalEntriesGrid";
import {CalculationSourceType} from "src/models/common/CalculationSource";
import {CalculationBuilderState} from "src/services/calculation-builder/CalculationBuilderState";
import {setupWebSocketEvents} from "src/components/calculation-builder/configure-journals/WebSocketEventHandler";
import WebSocketApi from "src/services/WebSocketApi";

export type ContextType = {
    state: JESettingsState,
    dispatch: React.Dispatch<TPEAction>,
    services: ServiceCollection,
    userProfile: UserProfile,
    calcBuilderState: CalculationBuilderState
}

interface ConfigureJournalsViewProps {
    services: ServiceCollection;
    isICRSRecord?: boolean;
}

interface LocationState {
    calcBuilderState?: CalculationBuilderState;
}

const ConfigureJournalsProvider = (props: any) => {
    const {state, dispatch, services, userProfile, calcBuilderState, children} = props;
    const providerValue = React.useMemo(() => ({
        state, dispatch, services, userProfile, calcBuilderState
    }), [state, dispatch, calcBuilderState]);
    return (
        <ConfigureJournalsContext.Provider value={providerValue}>
            {children}
        </ConfigureJournalsContext.Provider>
    );
}

export const ConfigureJournalsContext = React.createContext(null as unknown as ContextType);

export default function ConfigureJournalsView({ services, isICRSRecord = false }: ConfigureJournalsViewProps) {
    const [state, dispatch] = useReducerWithLogger(jeSettingsReducer, initialState);
    const location = useLocation<LocationState>();
    const calcBuilderState = location.state?.calcBuilderState || null;
    const {
        workflowAction,
        journalsAreValid,
        generatingReviewJournalsStatus,
        journals
    } = state;
    const history = useHistory();
    const { pathname } = useLocation();
    const { encodedCliRecordId } = useParams<{ encodedCliRecordId: string }>();
    const { globalDispatch, userProfile } = useContext(GlobalAppContext);
    
    const [recordNumber, setRecordNumber] = React.useState('');
   
    const getBaseUrl = useCallback(() => {
        return isICRSRecord ? '/icrs-calculations' : '/calculations';
    }, [isICRSRecord]);
    
    const calculationBuilderPath = useCallback(() => {
        history.push(`${getBaseUrl()}/builder/${encodedCliRecordId}`);
    }, [isICRSRecord, encodedCliRecordId, history]);

    useEffect(() => {
        if (!calcBuilderState) {
            // Redirect back to calculation builder
            history.replace(`${getBaseUrl()}/builder/${encodedCliRecordId}`);
        }

        const config = isICRSRecord ? CONSTANTS.PAGE_NAV.ICRS_CONFIGURE_JOURNALS : CONSTANTS.PAGE_NAV.CONFIGURE_JOURNALS;
        const recordNumber = calcBuilderState?.calculation?.calculationNumber;
        
        if (!recordNumber) {
            history.replace(`${getBaseUrl()}/builder/${encodedCliRecordId}`);
            return;
        }
        
        setRecordNumber(recordNumber);
        globalDispatch(GLOBAL_ACTIONS.SET_DYNAMIC_BREADCRUMB.withPayload(
            new DynamicBreadcrumb(
                pathname,
                config.BREADCRUMB.replace('cli', recordNumber),
                config.TITLE.replace('cli', recordNumber),
                config.DESCRIPTION
            )
        ));
     
    }, [calcBuilderState, pathname, globalDispatch, isICRSRecord]);

    useEffect(() => {
        if (journalsAreValid) {
            if (workflowAction != null && workflowAction == ACTIONS.GENERATE_REVIEW_JOURNALS) {
                const payload = {
                    calculationNumber: recordNumber,
                    s3PresignUrlRequired: true
                }
                services.atpWebSocketApiService.generateReviewJournals(payload);
                dispatch(ACTIONS.SET_GENERATE_REVIEW_JOURNALS_PAYLOAD.withPayload(payload));
            }
        }
    }, [journalsAreValid, workflowAction, recordNumber])

    useEffect(() => {
        const { listener, errorHandler } = setupWebSocketEvents(services, dispatch);
        return () => {
            // Anything in here is fired on component unmount.
            services.atpWebSocketApiService.removeListener('', listener);
            services.atpWebSocketApiService.removeListener(WebSocketApi.ConnectionEvent.ON_ERROR, errorHandler);
        }
    }, [])

    const generateReviewJournal = () => {
        dispatch(ACTIONS.TRIGGER_WORKFLOW.withPayload(ACTIONS.GENERATE_REVIEW_JOURNALS));
    }

    const generateReviewJournalLoading = React.useMemo(() => {
        return (
            generatingReviewJournalsStatus === CONSTANTS.GENERATE_REVIEW_JOURNAL_STATUS.STARTED ||
            generatingReviewJournalsStatus === CONSTANTS.GENERATE_REVIEW_JOURNAL_STATUS.IN_PROGRESS
        );
    }, [generatingReviewJournalsStatus]);

    return (
        <ConfigureJournalsProvider services={services} state={state} dispatch={dispatch} userProfile={userProfile} calcBuilderState={calcBuilderState}>
            <div style={{display: 'flex', justifyContent: 'flex-end', gap: '12px', marginBottom: '16px'}}>
                <Button onClick={calculationBuilderPath}>
                    Back to Calc Builder
                </Button>
                <Button
                    onClick={calculationBuilderPath}
                    disabled={generatingReviewJournalsStatus !== CONSTANTS.GENERATE_REVIEW_JOURNAL_STATUS.SUCCEEDED}
                >
                    Complete Step 4
                </Button>
                <Button
                    onClick={generateReviewJournal}
                    loading={generateReviewJournalLoading}
                    disabled={generateReviewJournalLoading || journals.length === 0}
                >
                    Generate Journals
                </Button>
            </div>
            {recordNumber && (
                <SpaceBetween direction="vertical" size="l">
                    <UploadJournalEntryFile
                        recordNumber={recordNumber}
                        services={services}
                        dispatch={dispatch}
                        userProfile={userProfile}
                    />
                    <JournalEntriesGrid
                        calculationSource={isICRSRecord ? CalculationSourceType.ICRS: CalculationSourceType.APTTUS}
                        recordNumber={recordNumber}
                        services={services}
                    />
                </SpaceBetween>
            )}
        </ConfigureJournalsProvider>
    );
}